import React, {useEffect, useState} from "react";
import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Icon,
  IconButton,
  Link,
  Text,
  useBreakpointValue,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useToken,
  VStack,
  Flex,
} from "@chakra-ui/react";
import {HamburgerIcon} from "@chakra-ui/icons";
import {FaChevronDown, FaChevronUp, FaCog,} from "react-icons/fa";
import {FiX} from "react-icons/fi";

import {Link as RouterLink, useLocation} from "react-router-dom";
import {motion, AnimatePresence} from "framer-motion"; // Enhanced Framer Motion imports
import IconMapping from "./IconMapping.jsx";
import API from "../../context/API";

// Animation variants for the sidebar
const sidebarVariants = {
  hidden: { x: -100, opacity: 0 },
  visible: { 
    x: 0, 
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
      duration: 0.8
    }
  }
};

const titleVariants = {
  hidden: { y: -20, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: {
      delay: 0.3,
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const linkVariants = {
  hidden: { x: -30, opacity: 0 },
  visible: (i) => ({
    x: 0,
    opacity: 1,
    transition: {
      delay: 0.5 + (i * 0.1),
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

const linkHoverVariants = {
  hover: { 
    x: 10,
    scale: 1.02,
    transition: { duration: 0.2 }
  },
  tap: { 
    scale: 0.98,
    transition: { duration: 0.1 }
  }
};

const iconVariants = {
  hover: { 
    rotate: 360,
    scale: 1.1,
    transition: { duration: 0.3 }
  }
};

const chevronVariants = {
  rotate: { 
    rotate: 180,
    transition: { duration: 0.3 }
  }
};

const roleMapping = {
  manager: "manager",
  staff: "staff",
  merchant_manager: "manager",
  merchant_staff: "staff",
};

const convertRoleToApi = (role) => roleMapping[role] || role;

const getRoleDisplayName = (role) => {
  const normalizedRole = convertRoleToApi(role);
  const roleDisplayMap = {
    merchant: "Merchant",
    admin: "Admin",
    manager: "Manager",
    staff: "Staff",
  };
  return roleDisplayMap[normalizedRole] || normalizedRole;
};

const sidebarLinks = [
  { icon: "FaHome", label: "Home", to: "/dashboard" },
  { icon: "FaUsers", label: "Merchants", to: "/merchants" },
  { icon: "FiLayers", label: "Merchants Stores", to: "/stores" },
  {
    icon: "FaMobile",
    label: "Devices",
    to: "#",
    children: [
      { label: "Device", to: "/devices", icon: "FaMobile" },
      { label: "Device Types", to: "/device-types", icon: "FaMobile" },
    ],
  },
  { icon: "GrAnalytics", label: "Analytics", to: "/analytics" },
  { icon: "HiDocumentReport", label: "Reports", to: "/reports" },
  { icon: "MdPayment", label: "Payments", to: "/payments" },
  { icon: "BiCategoryAlt", label: "Categories", to: "/merchantcategories" },
  { icon: "FiActivity", label: "Action Logs", to: "/logs" },
  { icon: "FiAward", label: "Loyalty", to: "/loyalty" },
  { icon: "FiBarChart", label: "Sales Analytics", to: "/sales-analytics" },
  {icon: "FiEnvelope", label: "Email Templates", to: "/email-templates"},
  {icon: "FaGavel", label: "Dispute management", to: "/dispute-management"},
  {icon: "FiTag", label: "Promotions", to: "/promotions"},

  // {
  //     icon: "MdMenuBook",
  //     label: "Menu",
  //     to: "#",
  //     children: [
  //         {icon: "MdMenuBook", label: "Items", to: "/items"},
  //         {icon: "CgColorPicker", label: "Customizations", to: "/customizations"},
  //     ],
  // },
];

const sidebarLinksForStaff = [
  { icon: "FaHome", label: "Home", to: "/dashboard" },
  { icon: "GrAnalytics", label: "Analytics", to: "/analytics" },
  { icon: "HiDocumentReport", label: "Reports", to: "/reports" },
  { icon: "MdMenuBook", label: "Items", to: "/items" },
  { icon: "CgColorPicker", label: "Customizations", to: "/customizations" },
  { icon: "TfiStatsUp", label: "Sales", to: "/sales" },
];

const MotionBox = motion(Box);
const MotionText = motion(Text);
const MotionIcon = motion(Icon);

const SidebarLink = ({
  icon,
  label,
  to,
  onClose,
  children,
  size = "md",
  colorMode,
  glassEffect,
  index = 0, // Add index for staggered animation
  onClick, // Keep the onClick prop
  selected, // Keep the selected prop
}) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handleToggle = (e) => {
    if (children) {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else {
      if (onClose && !children) {
        onClose();
      }
      // Call the onClick prop if it exists
      if (onClick) {
        onClick();
      }
    }
  };
  
  const activeBg = useToken(
    "colors",
    colorMode === "dark" ? "gray.700" : "gray.200"
  );
  const hoverBg = useToken(
    "colors",
    colorMode === "dark" ? "gray.600" : "gray.100"
  );
  const inactiveBg = "transparent";

  // Normalize the 'to' value to ensure it starts with a '/'
  const adjustedTo = to.startsWith("/") ? to : `/${to}`;

  // Adjusted active link determination
  const isLinkOrChildActive =
    location.pathname.startsWith(adjustedTo) ||
    (children &&
      children.some((child) => {
        const childAdjustedTo = child.to.startsWith("/")
          ? child.to
          : `/${child.to}`;
        return location.pathname.startsWith(childAdjustedTo);
      }));

  const sidebarIconSize = useBreakpointValue({ base: "3", md: "4" });

  const textColor = colorMode === "dark" ? "white" : "gray.600";
  const iconColor = colorMode === "dark" ? "white" : "gray.600";
  const bg = isLinkOrChildActive ? activeBg : isHovering ? hoverBg : inactiveBg;

  const iconSizes = {
    sm: "3",
    md: "4",
    lg: "5",
  };

  const fontSizes = {
    sm: "xs",
    md: "sm",
    lg: "md",
  };

  const currentIconSize = iconSizes[size];
  const currentFontSize = fontSizes[size];
  const hoverTextColor = colorMode === "dark" ? "gray.800" : "gray.800";

  const linkStyles = {
    _hover: {
      color: hoverTextColor,
      bg: hoverBg,
    },
    _activeLink: {
      color: colorMode === "dark" ? "teal.300" : "teal.600",
      bg: colorMode === "dark" ? "rgba(255, 255, 255, 0.2)" : "gray.200",
    },
  };

  const IconComponent = IconMapping[icon] || FaCog; // Default icon if not found

  return (
    <>
      <motion.div
        custom={index}
        variants={linkVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
        whileTap="tap"
      >
        <Link
          as={RouterLink}
          to={children ? "#" : to}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onClick={handleToggle}
          _hover={{ textDecoration: "none", ...linkStyles._hover }}
        >
          <MotionBox
            variants={linkHoverVariants}
            align="center"
            p={2}
            bg={bg}
            borderRadius="md"
            display="flex"
            alignItems="center"
            {...(isLinkOrChildActive && linkStyles._activeLink)}
            justifyContent="start"
            cursor="pointer"
            color={isLinkOrChildActive || isHovering ? textColor : iconColor}
            _hover={{
              boxShadow: "lg",
              transform: "translateX(5px)",
            }}
            transition="all 0.2s ease"
          >
            <MotionIcon
              as={IconComponent}
              boxSize={currentIconSize}
              variants={iconVariants}
              whileHover="hover"
            />
            <MotionText 
              ml={3} 
              fontSize={currentFontSize}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 + (index * 0.1), duration: 0.5 }}
            >
              {label}
            </MotionText>
            {children && (
              <MotionIcon
                as={isOpen ? FaChevronUp : FaChevronDown}
                color={textColor}
                boxSize={sidebarIconSize}
                ml="auto"
                animate={isOpen ? "rotate" : "initial"}
                variants={chevronVariants}
              />
            )}
          </MotionBox>
        </Link>
      </motion.div>
      
      {children && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <VStack pl={4} align="stretch" spacing={1}>
                {children.map((childLink, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1, duration: 0.3 }}
                  >
                    <SidebarLink
                      icon={childLink.icon}
                      label={childLink.label || childLink.page}
                      to={childLink.to || childLink.slug}
                      hoverBg={hoverBg}
                      colorMode={colorMode}
                      size="sm"
                      index={index + idx + 1}
                    />
                  </motion.div>
                ))}
              </VStack>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </>
  );
};

const SidebarContent = ({
  onClose,
  colorMode,
  menuItems,
  role,
  glassEffect,
  selectedLink,
  onLinkClick,
}) => {
  const sidebarHeadingSize = useBreakpointValue({ base: "lg", md: "xl" });
  const marginingMobile = useBreakpointValue({ base: 4, md: 1 });

  const filteredMenuItems = menuItems
    .filter((item) => {
      const access = item.dashboardAccess.find(
        (access) => access.role === role || access.role === convertRoleToApi(role)
      );
      return access?.R;
    })
    .filter((item) => {
      const slug = (item.slug || "").replace(/^\//, "") || "";
      if (slug === "merchantmanager") return role === "merchant";
      return true;
    });

  return (
    <VStack align="stretch" spacing={0} h="100%" position="relative">
      {/* Enhanced title with animation - Fixed at top */}
      <Box flexShrink={0} mb={2}>
        <motion.div
          variants={titleVariants}
          initial="hidden"
          animate="visible"
        >
          <MotionText
            fontSize={sidebarHeadingSize}
            fontWeight="bold"
            textAlign="left"
            ml={1}
            mb={2}
            bgGradient="linear(to-r, teal.400, purple.500)"
            bgClip="text"
            _hover={{
              bgGradient: "linear(to-r, purple.500, teal.400)",
              transition: "all 0.3s ease"
            }}
          >
            Starntop {getRoleDisplayName(role)}
          </MotionText>
        </motion.div>
      </Box>
      
      {/* Scrollable menu items container */}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        pr={2}
        sx={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: colorMode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: colorMode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
          },
        }}
      >
        <VStack align="stretch" spacing={1} m={marginingMobile}>
          {/* Animated menu items */}
          {role === "admin"
            ? sidebarLinks.map((link, index) => (
                <SidebarLink
                  glassEffect={glassEffect}
                  key={index}
                  colorMode={colorMode}
                  icon={link.icon}
                  label={link.label}
                  to={link.to}
                  onClose={onClose}
                  onClick={() => onLinkClick && onLinkClick(link.to)}
                  children={link.children}
                  index={index}
                />
              ))
            : filteredMenuItems.map((link, index) => (
                <SidebarLink
                  glassEffect={glassEffect}
                  key={index}
                  colorMode={colorMode}
                  icon={link.icon}
                  label={link.slug === "/merchantmanager" ? "Manager" : link.page}
                  to={link.slug}
                  selected={link.slug === selectedLink}
                  onClick={() => onLinkClick && onLinkClick(link.slug)}
                  onClose={onClose}
                  children={link.children}
                  index={index}
                />
              ))}
          
          {/* Add Dispute Management for merchant role */}
          {(role === "merchant" || role === "manager") && (
            <SidebarLink
              glassEffect={glassEffect}
              colorMode={colorMode}
              icon="FaGavel"
              label="Dispute management"
              to="/dispute-management"
              onClose={onClose}
              onClick={() => onLinkClick && onLinkClick("/dispute-management")}
              index={filteredMenuItems?.length || 0}
            />
          )}

          {/* Add Role Management for merchant and admin roles - commented out for now */}
          {/* {(role === "merchant" || role === "admin") && (
            <SidebarLink
              glassEffect={glassEffect}
              colorMode={colorMode}
              icon="FiShield"
              label="Role Management"
              to="/role-management"
              onClose={onClose}
              onClick={() => onLinkClick && onLinkClick("/role-management")}
              index={(filteredMenuItems?.length || 0) + 1}
            />
          )} */}
          
          {role === "staff" &&
            sidebarLinksForStaff.map((link, index) => (
              <SidebarLink
                glassEffect={glassEffect}
                key={index}
                colorMode={colorMode}
                icon={link.icon}
                label={link.label}
                to={link.to}
                onClose={onClose}
                onClick={() => onLinkClick && onLinkClick(link.to)}
                index={index + (filteredMenuItems?.length || 0)}
              />
            ))}
          
          {/* Logout button with special animation */}
          <Box mt="auto" mb={2}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SidebarLink
                glassEffect={glassEffect}
                colorMode={colorMode}
                icon="FaSignOutAlt"
                label="Logout"
                to="/logout"
                onClose={onClose}
                // index={999} // Special index for logout
              />
            </motion.div>
          </Box>
        </VStack>
      </Box>
    </VStack>
  );
};

const Sidebar = ({ role, setDynamicRoutes }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const sidebarWidth = "250px";
  const { colorMode } = useColorMode();
  const [menuItems, setMenuItems] = useState([]);
  const location = useLocation();

  const borderColor = useColorModeValue("gray.300", "gray.600");

  const [selectedLink, setSelectedLink] = useState(location.pathname);

  const sidebarStyles = {
    bg: colorMode === "dark" ? "gray.800" : "white",
    backdropFilter: colorMode === "dark" ? "blur(10px)" : "none",
    boxShadow: colorMode === "dark" ? "0 4px 6px rgba(0, 0, 0, 0.3)" : "sm",
    color: colorMode === "dark" ? "white" : "gray.800",
  };

  const glassEffect = {
    bg: colorMode === "dark" ? "rgba(255, 255, 255, 0.05)" : "white",
    backdropFilter: colorMode === "dark" ? "blur(10px)" : "none",
    boxShadow: colorMode === "dark" ? "0 4px 6px rgba(0, 0, 0, 0.1)" : "sm",
  };

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const endpoint = role === "admin" ? "/dashboard" : "/dashboard/menu";
        const response = await API.get(endpoint);

        if (response.data.success) {
          let filteredItems = response.data.data;

          // Filter items based on the role
          if (role === "manager") {
            filteredItems = filteredItems.filter((item) =>
              [
                "dashboard",
                "reports",
                "staff",
                "analytics",
                "items",
                "customizations",
                "creator program",
              ].includes(item.page.toLowerCase())
            );
          } else if (role === "merchant") {
            filteredItems = filteredItems.filter((item) => {
              const normalizedPage = item.page.toLowerCase();
              return [
                "dashboard",
                //"reports",
                "users",
                //"items",
                "kiosk design",
                "orders",
                "analytics",
                "stores",
                "loyalty",
                //"operations",
                "device assignment",
                "active session & devices",
                "action logs",
                "menu",
                "dispute management",
                "disputes",
                "creator program",
              ].includes(normalizedPage);
            });
          }

          setMenuItems(filteredItems);
          setDynamicRoutes(filteredItems);
        }
      } catch (error) {
        console.error("Error fetching menu items:", error);
      }
    };

    fetchMenuItems();
  }, [role, setDynamicRoutes]);

  const handleLinkClick = (slug) => {
    setSelectedLink(slug);
    onClose();
  };

  const displaySidebar = useBreakpointValue({ base: "none", md: "block" });

  return (
    <>
      {/* Enhanced hamburger menu button with animation */}
      <motion.div
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <IconButton
          aria-label="Open Menu"
          icon={<HamburgerIcon />}
          onClick={onOpen}
          display={{ base: "inline-flex", md: "none" }}
          position="fixed"
          zIndex="overlay"
          m={4}
          top="0px"
          left="0px"
          _hover={{
            bg: "teal.500",
            color: "white",
            transform: "rotate(90deg)",
            transition: "all 0.3s ease"
          }}
        />
      </motion.div>

      {/* Enhanced drawer for mobile view */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <Flex
            position="absolute"
            top={4}
            right={4}
            zIndex={1000}
          >
            <IconButton
              aria-label="Close Menu"
              icon={<FiX />}
              onClick={onClose}
              size="sm"
              variant="ghost"
              colorScheme="gray"
              _hover={{
                bg: "gray.200",
                color: "gray.800",
              }}
            />
          </Flex>
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
          >
            <SidebarContent
              menuItems={menuItems}
              role={role}
              onClose={onClose}
              colorMode={colorMode}
              onLinkClick={handleLinkClick}
              glassEffect={glassEffect}
              selectedLink={selectedLink}
            />
          </motion.div>
        </DrawerContent>
      </Drawer>

      {/* Enhanced permanent sidebar for larger screens */}
      <motion.div
        variants={sidebarVariants}
        initial="hidden"
        animate="visible"
        style={{
          display: displaySidebar,
          position: "fixed",
          left: 0,
          top: 0,
          height: "100vh",
          width: sidebarWidth,
          zIndex: 10
        }}
      >
        <Box
          as="nav"
          h="100vh"
          w="100%"
          bg={glassEffect}
          p={4}
          transition="all 0.3s ease"
          {...sidebarStyles}
        >
          <SidebarContent
            menuItems={menuItems}
            role={role}
            colorMode={colorMode}
            onLinkClick={handleLinkClick}
            glassEffect={glassEffect}
            selectedLink={selectedLink}
          />
          
          {/* Enhanced vertical divider with animation */}
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "100%" }}
            transition={{ delay: 1.0, duration: 0.8 }}
          >
            <Box
              position="absolute"
              right="0"
              top="0"
              bottom="0"
              width="0.5px"
              bg={borderColor}
            />
          </motion.div>
        </Box>
      </motion.div>
    </>
  );
};

export default Sidebar;
